import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InfoBox from '@fingo/lib/components/boxes/InfoBox';
import { CurrencyPillSelect } from '@fingo/lib/components/selects';
import { REQUESTING_PLATFORM_TO_CODES } from '@fingo/lib/constants/requesting-platform';
import { UPLOAD_FACTORING_INVOICES } from '@fingo/lib/graphql/invoices/mutations';
import useExcelTemplateDownloader from '@fingo/lib/hooks/useExcelTemplateDownloader';
import useSelectedCompany from '@fingo/lib/hooks/useSelectedCompany';
import useSnackBars from '@fingo/lib/hooks/useSnackBars';
import UploadFlowDialog from './UploadFlowDialog';
import UploadNominaDropZone from './UploadNominaDropZone';
import { UPLOAD_NOMINA_METHOD_LABEL } from './UploadInvoicesMethodDialog';

const getTemplateColumns = (currency) => [
  'Folio',
  'DTE',
  currency ? `Monto en ${currency}` : 'Monto',
  'Rut deudor',
  'Fecha de emisión',
  ...(currency === 'USD' ? ['Total en CLP'] : []),
];

const getUploadErrorMessage = (message) => {
  if (message.includes('does not exist or you cannot upload')) {
    return 'No tienes permiso para subir facturas para esta empresa.';
  }
  if (message.includes('needs a column with each invoice')) {
    return "El archivo en USD debe incluir la columna 'Total en CLP'.";
  }
  if (message.includes('must share the same currency')) {
    return 'Todas las filas del archivo deben tener la misma moneda.';
  }
  if (message.includes('cannot hold more than')) {
    return 'El archivo supera la cantidad máxima de facturas permitida.';
  }
  if (message.includes('cannot hold the same invoice twice')) {
    return 'El archivo repite una o más facturas.';
  }
  if (message.includes('Invalid roster:')) {
    return 'El archivo tiene un error de formato. Revísalo e intenta nuevamente.';
  }
  return 'Ocurrió un error al subir la nómina. Intenta nuevamente.';
};

const UploadNominaDialog = ({ open, onClose, onBack }) => {
  const [files, setFiles] = useState([]);
  const [currency, setCurrency] = useState('');
  const templateColumns = getTemplateColumns(currency);
  const selectedCompany = useSelectedCompany();
  const { addAlert } = useSnackBars();
  const [downloadTemplate, { loading: loadingTemplate }] = useExcelTemplateDownloader(`plantilla_nomina_${currency}`);
  const [uploadFactoringInvoices, { loading }] = useMutation(UPLOAD_FACTORING_INVOICES, {
    variables: { companyId: selectedCompany?.id, file: files[0], currency },
    onCompleted: () => {
      addAlert({
        id: 'upload-nomina-success',
        message: 'Nómina subida correctamente. Te enviaremos un correo cuando termine de procesarse.',
        severity: 'success',
      });
      setFiles([]);
      setCurrency('');
      onClose();
    },
    onError: (error) => {
      addAlert({
        id: 'upload-nomina-error',
        message: getUploadErrorMessage(error.message),
        severity: 'error',
      });
    },
  });

  return (
    <UploadFlowDialog
      open={open}
      onClose={onClose}
      title={(
        <Typography variant="medium_2" fontWeight={700} align="center" sx={{ color: 'gray.A600' }}>
          {UPLOAD_NOMINA_METHOD_LABEL}
        </Typography>
      )}
      actions={(
        <Stack direction="row" spacing={1} justifyContent="center" width="100%">
          <Button variant="outlined" color="primary" onClick={onBack} size="large_1">
            <Typography variant="small_3" fontWeight={700} sx={{ color: 'primary.main' }}>
              Atrás
            </Typography>
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large_1"
            onClick={() => uploadFactoringInvoices()}
            disabled={!files.length || !selectedCompany || !currency || loading}
          >
            <Typography variant="small_3" fontWeight={700} sx={{ color: 'white' }}>
              Continuar
            </Typography>
          </Button>
        </Stack>
      )}
    >
      <Stack spacing={3} sx={{ width: 663, maxWidth: '100%' }}>
        <UploadNominaDropZone files={files} setFiles={setFiles} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="small_3" sx={{ color: 'common.black' }}>
            Selecciona la moneda de las facturas:
          </Typography>
          <CurrencyPillSelect
            currency={currency}
            setCurrency={setCurrency}
            requestingPlatform={REQUESTING_PLATFORM_TO_CODES.ITAU}
          />
        </Stack>
        <InfoBox
          description="El archivo debe tener los siguientes campos:"
          list={templateColumns}
        />
        <Button
          variant="text"
          color="primary"
          onClick={() => downloadTemplate({ variables: { columns: templateColumns } })}
          disabled={!currency || loadingTemplate}
          sx={{ alignSelf: 'center' }}
        >
          <Typography variant="small_3" fontWeight={700} color="inherit">
            Descarga la plantilla en .XLSX
          </Typography>
        </Button>
      </Stack>
    </UploadFlowDialog>
  );
};

UploadNominaDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default UploadNominaDialog;
